import { XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { formatDate } from 'date-fns';
import { motion } from 'framer-motion';
import { Bot } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/catalyst/button';
import { Tooltip } from '@/components/ui/v3/Tooltip';
import { useMatter } from '@/hooks/useMatter';
import {
  ChangeTreatmentRecordRequestStatusLogSchema,
  TreatmentRecordRequestSchema,
  TreatmentRecordRequestStatus,
} from '@/services/api/generated/treatmentRecordRequestsService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';
import { MedicalProvider } from '@/services/types/treatment-types';

import {
  BlockedState,
  ResolveBlockersButton,
} from '../tabs/records/ResolveBlockersButton';

type PizzaTrackerStepState = 'complete' | 'up_next' | 'not_started';

const useCompletedDate = (
  request: TreatmentRecordRequestSchema,
  provider: MedicalProvider,
  status: TreatmentRecordRequestStatus | undefined,
) => {
  const { matterId } = useMatter();
  const { data: logs } =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsAuditLogsListQuery(
      {
        matterId,
        providerId: provider.id,
        requestId: request.id,
      },
    );

  if (!status) {
    return undefined;
  }

  if (logs?.length === 0 || !logs) {
    return undefined;
  }

  const logsWithStatus = logs.find(
    (log) => log.action === 'change_status' && log.previous_status === status,
  );

  return logsWithStatus?.created_at;
};

export const useUpdateRequestStatus = ({
  requestId,
  providerId,
}: {
  requestId: string;
  providerId: string;
}) => {
  const { matterId } = useMatter();

  const [_changeStatus, { isLoading }] =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsChangeStatusCreateMutation();

  return {
    changeStatus: async (
      newStatus: TreatmentRecordRequestStatus,
      log?: ChangeTreatmentRecordRequestStatusLogSchema,
      forceOverride = false,
      attachments?: string[],
    ) => {
      await _changeStatus({
        matterId,
        providerId,
        requestId,
        changeTreatmentRecordRequestStatusSchema: {
          new_status: newStatus,
          log,
          override_requirements: forceOverride,
          ...(attachments && attachments.length > 0
            ? { attachments }
            : {}),
        },
      })
        .catch((error) => {
          toast.error('Failed to update status');
          throw error;
        })
        .then(() => {
          toast.success('Status updated');
        });
    },
    isLoading,
  };
};

const BaseFinchy = ({ className }: { className: string }) => {
  return <Bot className={clsx('w-4 h-4', className)} />;
};

// Swirl transition wrapper
const TransitionWrapper = ({
  children,
  isTransitioning,
  prevState,
  state,
}: {
  children: React.ReactNode;
  isTransitioning: boolean;
  prevState: string;
  state: string;
}) => {
  if (isTransitioning) {
    return (
      <motion.div
        initial={{ scale: 1, rotate: 0, opacity: 1 }}
        animate={{
          scale: [1, 0.3, 0, 0, 0.3, 1.1, 1],
          rotate: [0, 180, 360, 540, 720, 720, 720],
          opacity: [1, 1, 0, 0, 1, 1, 1],
        }}
        transition={{
          duration: 0.6,
          times: [0, 0.2, 0.3, 0.5, 0.6, 0.8, 1],
          ease: 'easeInOut',
        }}
      >
        {children}
      </motion.div>
    );
  }

  // Tada entrance animation after transition
  if (prevState !== state && !isTransitioning) {
    return (
      <motion.div
        initial={{ scale: 0, rotate: 0 }}
        animate={{
          scale: [0, 1.2, 0.9, 1.05, 1],
          rotate: [0, -10, 10, -5, 0],
        }}
        transition={{
          duration: 0.5,
          times: [0, 0.3, 0.5, 0.7, 1],
          ease: 'easeOut',
        }}
      >
        {children}
      </motion.div>
    );
  }

  return children;
};

const Finchy = ({ request }: { request: TreatmentRecordRequestSchema }) => {
  const agentStatus = request.metadata?.automation_state_v2?.status;
  const [prevState, setPrevState] = useState<string>('idle');
  const [isTransitioning, setIsTransitioning] = useState(false);

  const state = useMemo(() => {
    if (agentStatus === 'blocked') return 'blocked';
    if (agentStatus === 'running') return 'running';
    if (agentStatus === 'sleeping') return 'sleeping';
    if (agentStatus === 'failed') return 'failed';
    return 'idle';
  }, [agentStatus]);

  // Detect state changes and trigger transition animation
  useEffect(() => {
    if (state !== prevState) {
      setIsTransitioning(true);
      const timer = setTimeout(() => {
        setPrevState(state);
        setIsTransitioning(false);
      }, 600); // Duration of swirl animation
      return () => clearTimeout(timer);
    }
    return () => {};
  }, [state, prevState]);

  if (state === 'blocked') {
    return (
      <TransitionWrapper
        isTransitioning={isTransitioning}
        prevState={prevState}
        state={state}
      >
        <div className="relative w-4 h-4">
          {/* Waving bot asking for help */}
          <motion.div
            animate={{
              rotate: [0, -10, 10, -10, 0],
              y: [0, -1, 0],
            }}
            transition={{
              duration: 0.8,
              repeat: Infinity,
              repeatDelay: 1.5,
              ease: 'easeInOut',
            }}
          >
            <BaseFinchy className="text-amber-500" />
          </motion.div>

          {/* Question mark bubble */}
          <motion.div
            className="absolute -top-2 -right-1"
            animate={{
              y: [0, -1, 0],
              opacity: [0.8, 1, 0.8],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          >
            <div className="w-3 h-3 bg-amber-100 rounded-full flex items-center justify-center border border-amber-300">
              <span className="text-amber-600 text-[8px] font-bold">?</span>
            </div>
          </motion.div>
        </div>
      </TransitionWrapper>
    );
  }

  if (state === 'sleeping') {
    return (
      <TransitionWrapper
        isTransitioning={isTransitioning}
        prevState={prevState}
        state={state}
      >
        <div className="relative">
          <motion.div
            animate={{
              y: [0, 2, 0],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          >
            <BaseFinchy className="text-blue-500" />
          </motion.div>

          {/* First Z */}
          <motion.span
            className="absolute -top-2 left-3 text-blue-400 text-[10px] font-bold"
            animate={{
              opacity: [0, 1, 1, 0],
              y: [0, -8],
              x: [0, 2],
            }}
            transition={{
              duration: 2.5,
              repeat: Infinity,
              ease: 'easeOut',
              delay: 0,
            }}
          >
            z
          </motion.span>

          {/* Second Z */}
          <motion.span
            className="absolute -top-1 left-4 text-blue-400 text-[8px] font-bold"
            animate={{
              opacity: [0, 1, 1, 0],
              y: [0, -10],
              x: [0, 3],
            }}
            transition={{
              duration: 2.5,
              repeat: Infinity,
              ease: 'easeOut',
              delay: 0.5,
            }}
          >
            z
          </motion.span>

          {/* Third Z */}
          <motion.span
            className="absolute top-0 left-5 text-blue-400 text-[6px] font-bold"
            animate={{
              opacity: [0, 1, 1, 0],
              y: [0, -12],
              x: [0, 4],
            }}
            transition={{
              duration: 2.5,
              repeat: Infinity,
              ease: 'easeOut',
              delay: 1,
            }}
          >
            z
          </motion.span>
        </div>
      </TransitionWrapper>
    );
  }

  if (state === 'running') {
    return (
      <TransitionWrapper
        isTransitioning={isTransitioning}
        prevState={prevState}
        state={state}
      >
        <div className="relative w-4 h-4">
          {/* Pulsing bot */}
          <motion.div
            className="absolute inset-0"
            animate={{
              scale: [1, 1.05, 1],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          >
            <BaseFinchy className="text-blue-500" />
          </motion.div>

          {/* Orbiting circle */}
          <svg className="absolute -left-2 -top-2 w-8 h-8" viewBox="0 0 32 32">
            <motion.circle
              cx="16"
              cy="16"
              r="14"
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
              className="text-blue-400"
              strokeDasharray="4 4"
              animate={{
                rotate: [0, 360],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: [0.4, 0, 0.6, 1],
                times: [0, 1],
              }}
              style={{
                transformOrigin: '16px 16px',
              }}
            />
          </svg>
        </div>
      </TransitionWrapper>
    );
  }

  if (state === 'failed') {
    return (
      <TransitionWrapper
        isTransitioning={isTransitioning}
        prevState={prevState}
        state={state}
      >
        <div className="relative w-4 h-4">
          {/* Shaking bot showing distress */}
          <motion.div
            animate={{
              x: [0, -1, 1, -1, 1, 0],
              rotate: [0, -5, 5, -5, 5, 0],
            }}
            transition={{
              duration: 0.5,
              repeat: Infinity,
              repeatDelay: 2,
              ease: 'easeInOut',
            }}
          >
            <BaseFinchy className="text-red-500" />
          </motion.div>

          {/* X mark indicator */}
          <motion.div
            className="absolute -top-1.5 -right-1.5"
            initial={{ scale: 0, rotate: -180 }}
            animate={{
              scale: [0, 1.2, 1],
              rotate: [180, 0],
              opacity: [0, 1, 1, 1, 0.8],
            }}
            transition={{
              duration: 0.5,
              repeat: Infinity,
              repeatDelay: 2.5,
              ease: 'easeOut',
            }}
          >
            <div className="w-3 h-3 bg-red-100 rounded-full flex items-center justify-center border border-red-300">
              <span className="text-red-600 text-[8px] font-bold leading-none">
                <XMarkIcon className="w-3 h-3" />
              </span>
            </div>
          </motion.div>
        </div>
      </TransitionWrapper>
    );
  }

  return (
    <TransitionWrapper
      isTransitioning={isTransitioning}
      prevState={prevState}
      state={state}
    >
      <BaseFinchy className="text-blue-500" />
    </TransitionWrapper>
  );
};

const MaybeTooltip = ({
  children,
  content,
}: {
  children: React.ReactNode;
  content: string | null | undefined;
}) => {
  if (!content) {
    return children;
  }
  return (
    <Tooltip content={content} side="top" align="center">
      {children}
    </Tooltip>
  );
};

const TopRightMenu = ({
  request,
}: {
  request: TreatmentRecordRequestSchema;
}) => {
  const reason = request.metadata?.automation_state_v2?.details;

  return (
    <div className="absolute top-2 right-2 rounded-full p-1 bg-white flex items-center gap-2 border border-gray-200">
      <MaybeTooltip content={reason}>
        <div>
          <Finchy request={request} />
        </div>
      </MaybeTooltip>
    </div>
  );
};

interface PizzaTrackerStepProps {
  state: PizzaTrackerStepState;
  title: string;
  children?: React.ReactNode;
  provider: MedicalProvider;
  request: TreatmentRecordRequestSchema;
  status: TreatmentRecordRequestStatus;
}

export const PizzaTrackerStep = ({
  state,
  title,
  children,
  status,
  provider,
  request,
}: PizzaTrackerStepProps) => {
  const styles: Record<
    PizzaTrackerStepState,
    {
      taglineClassName: string;
      titleClassName: string;
      backgroundClassName: string;
      borderClassName?: string;
    }
  > = {
    complete: {
      taglineClassName: ' text-white/70 tracking-wide',
      titleClassName: 'text-white',
      backgroundClassName: 'bg-[#190E5D]',
    },
    up_next: {
      taglineClassName: ' text-[#3E21F3] tracking-wide',
      titleClassName: 'text-[#190E5D]',
      backgroundClassName: 'bg-[#F1EFFF] border border-[#E0DEEF]',
    },
    not_started: {
      taglineClassName: 'text-black/40 tracking-wide',
      titleClassName: 'text-black/60',
      backgroundClassName: 'bg-[#F7F7F7] border border-[#E0E0E0]',
    },
  };

  const completedDate = useCompletedDate(request, provider, status);
  let tagline;
  if (state === 'up_next') {
    tagline = 'Next Step';
  }
  if (state === 'complete') {
    tagline = completedDate
      ? formatDate(completedDate, 'MMM d, yyyy')
      : 'Completed';
  }

  const style = styles[state];

  const isAutomated =
    request.actor === 'agent' &&
    request.metadata?.automation_state_v2?.status !== 'idle' &&
    request.metadata?.automation_state_v2;

  return (
    <div
      className={clsx(
        'rounded-xl flex flex-col py-4 gap-2 px-8 min-w-64 min-h-32 justify-between relative',
        style.backgroundClassName,
      )}
    >
      {/* Automation Logo  */}
      {state === 'up_next' && isAutomated && <TopRightMenu request={request} />}

      {/* header / tagline  */}
      <div className="flex flex-col gap-1">
        {tagline && (
          <span
            className={clsx(
              style.taglineClassName,
              'text-[10px] uppercase font-semibold',
            )}
          >
            {tagline}
          </span>
        )}

        <span className={clsx(style.titleClassName, 'text-sm font-medium')}>
          {title}
        </span>
      </div>
      {children}
    </div>
  );
};

const TrackerContent = ({
  request,
  provider,
  state,
  actionText,
  handleAction,
  isActionInProgress,
}: {
  request: TreatmentRecordRequestSchema;
  provider: MedicalProvider;
  state: PizzaTrackerStepState;
  actionText: string;
  handleAction: () => void;
  isActionInProgress: boolean;
}) => {
  const isAutomated =
    request.actor === 'agent' &&
    request.metadata?.automation_state_v2?.status !== 'idle';

  const hasBlockers =
    request.metadata?.automation_state_v2?.status === 'blocked' &&
    request.metadata?.automation_state_v2?.blocked_by?.length &&
    request.metadata?.automation_state_v2?.blocked_by?.length > 0 &&
    state === 'up_next';

  const isUpNext = state === 'up_next' || state === 'not_started';

  const handleOverride = () => {
    handleAction();
  };

  if (isAutomated && hasBlockers) {
    return (
      <div className="flex gap-0">
        <ResolveBlockersButton
          request={request}
          blockedState={request.metadata?.automation_state_v2 as BlockedState}
          provider={provider}
          onOverride={handleOverride}
          isDisabled={isActionInProgress}
        />
      </div>
    );
  }
  if (isUpNext) {
    return (
      <Button
        color="indigo-steel"
        onClick={handleAction}
        disabled={isActionInProgress}
      >
        {actionText}
      </Button>
    );
  }

  return null;
};

export interface BaseRequestStepProps {
  state: PizzaTrackerStepState;
  request: TreatmentRecordRequestSchema;
  provider: MedicalProvider;
  status: TreatmentRecordRequestStatus;
  nextStatus: TreatmentRecordRequestStatus;
  actionText: string;
  title: string;
  renderCompletionModal?: (props: {
    isOpen: boolean;
    onClose: () => void;
    request: TreatmentRecordRequestSchema;
    provider: MedicalProvider;
  }) => React.ReactNode;
}

export const BaseRequestStep = ({
  state,
  request,
  provider,
  status,
  actionText,
  nextStatus,
  title,
  renderCompletionModal,
}: BaseRequestStepProps) => {
  const [isActionInProgress, setIsActionInProgress] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);

  const { changeStatus } = useUpdateRequestStatus({
    requestId: request.id,
    providerId: provider.id,
  });

  const hasBlockers = !!(
    request.metadata?.automation_state_v2?.status === 'blocked' &&
    request.metadata?.automation_state_v2?.blocked_by?.length &&
    request.metadata?.automation_state_v2?.blocked_by?.length > 0
  );

  const handleAction = async () => {
    // If there's a custom modal, show it instead of directly performing the action
    if (renderCompletionModal) {
      setShowCompletionModal(true);
    } else {
      // Direct action without modal - just change status
      setIsActionInProgress(true);
      try {
        await changeStatus(nextStatus, undefined, hasBlockers);
      } catch (error) {
        toast.error('Failed to change status');
      } finally {
        setIsActionInProgress(false);
      }
    }
  };

  const handleModalClose = () => {
    setShowCompletionModal(false);
  };

  return (
    <>
      <PizzaTrackerStep
        state={state}
        title={title}
        provider={provider}
        request={request}
        status={status}
      >
        <TrackerContent
          request={request}
          provider={provider}
          state={state}
          actionText={actionText}
          handleAction={handleAction}
          isActionInProgress={isActionInProgress}
        />
      </PizzaTrackerStep>
      {renderCompletionModal &&
        renderCompletionModal({
          isOpen: showCompletionModal,
          onClose: handleModalClose,
          request,
          provider,
        })}
    </>
  );
};
